import { useState, useEffect } from 'react';
import { Award, ExternalLink, Loader2 } from 'lucide-react';
import { getPublicCertifications } from '../services/certificationsApi';
import type { PublicCertification } from '../services/certificationsApi';
import { LABELS } from '../data/labels';

interface Props {
    labels: typeof LABELS['fr'];
}

export function CertificationsBadgesSection({ labels }: Props) {
    const [certs, setCerts] = useState<PublicCertification[]>([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        async function load() {
            try {
                const data = await getPublicCertifications();
                setCerts(data);
            } catch (err) {
                console.error(err);
            } finally {
                setLoading(false);
            }
        }
        load();
    }, []);

    if (loading) {
        return (
            <div className="flex justify-center items-center h-64">
                <Loader2 className="w-8 h-8 animate-spin text-[#1f75cb]" />
            </div>
        );
    }

    return (
        <div className="space-y-6">
            <h2 className="section-title mb-6">
                <Award className="w-5 h-5 text-[#fc6d26]" />
                {labels.badges || "Certifications & Badges"}
            </h2>

            {certs.length === 0 ? (
                <div className="card p-12 text-center">
                    <Award className="w-12 h-12 text-[#e5e5e5] mx-auto mb-4" />
                    <p className="text-[#5e5e5e]">Aucune certification n'a été ajoutée pour le moment.</p>
                </div>
            ) : (
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                    {/* Certifications Column */}
                    <div>
                        <h3 className="text-xl font-bold text-[#1d1d1f] mb-4">Certifications</h3>
                        <div className="space-y-4">
                            {certs.filter(c => c.type !== 'badge').map(cert => (
                                <div key={cert.id} className="card card-hover p-5 flex flex-col h-full bg-white relative">
                                    <div className="flex items-start gap-4">
                                        <div className="w-16 h-16 rounded-lg bg-[#f5f5f7] flex items-center justify-center overflow-hidden shrink-0 shadow-inner">
                                            {cert.imageUrl ? (
                                                <img src={cert.imageUrl} alt={cert.title} className="w-full h-full object-contain p-2" />
                                            ) : (
                                                <Award className="w-8 h-8 text-[#5e5e5e]" />
                                            )}
                                        </div>
                                        <div className="flex-1 min-w-0">
                                            <h4 className="font-bold text-[#1d1d1f] leading-tight mb-1 truncate" title={cert.title}>{cert.title}</h4>
                                            <p className="text-sm font-medium text-[#1f75cb] truncate">{cert.issuer}</p>
                                        </div>
                                    </div>

                                    {cert.credentialId && (
                                        <p className="text-xs text-[#8e8e93] font-mono mt-3 mb-1">ID: {cert.credentialId}</p>
                                    )}

                                    {cert.description && (
                                        <p className="text-sm text-[#5e5e5e] line-clamp-3 mt-2">{cert.description}</p>
                                    )}

                                    <div className="mt-4 pt-4 border-t border-[#e5e5e5] flex items-center justify-between text-xs text-[#5e5e5e]">
                                        <span>{new Date(cert.date).toLocaleDateString()}</span>
                                        {cert.url && (
                                            <a
                                                href={cert.url}
                                                target="_blank"
                                                rel="noopener noreferrer"
                                                className="flex items-center gap-1 text-[#1f75cb] font-medium hover:underline"
                                            >
                                                {labels.verified || "Vérifier"} <ExternalLink className="w-3 h-3" />
                                            </a>
                                        )}
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>

                    {/* Badges Column */}
                    <div>
                        <h3 className="text-xl font-bold text-[#1d1d1f] mb-4">Badges</h3>
                        <div className="space-y-4">
                            {certs.filter(c => c.type === 'badge').map(cert => (
                                <div key={cert.id} className="card card-hover p-5 flex flex-col h-full bg-white relative">
                                    <div className="flex items-start gap-4">
                                        <div className="w-16 h-16 rounded-lg bg-[#f5f5f7] flex items-center justify-center overflow-hidden shrink-0 shadow-inner">
                                            {cert.imageUrl ? (
                                                <img src={cert.imageUrl} alt={cert.title} className="w-full h-full object-contain p-2" />
                                            ) : (
                                                <Award className="w-8 h-8 text-[#5e5e5e]" />
                                            )}
                                        </div>
                                        <div className="flex-1 min-w-0">
                                            <h4 className="font-bold text-[#1d1d1f] leading-tight mb-1 truncate" title={cert.title}>{cert.title}</h4>
                                            <p className="text-sm font-medium text-[#1f75cb] truncate">{cert.issuer}</p>
                                        </div>
                                    </div>

                                    {cert.credentialId && (
                                        <p className="text-xs text-[#8e8e93] font-mono mt-3 mb-1">ID: {cert.credentialId}</p>
                                    )}

                                    {cert.description && (
                                        <p className="text-sm text-[#5e5e5e] line-clamp-3 mt-2">{cert.description}</p>
                                    )}

                                    <div className="mt-4 pt-4 border-t border-[#e5e5e5] flex items-center justify-between text-xs text-[#5e5e5e]">
                                        <span>{new Date(cert.date).toLocaleDateString()}</span>
                                        {cert.url && (
                                            <a
                                                href={cert.url}
                                                target="_blank"
                                                rel="noopener noreferrer"
                                                className="flex items-center gap-1 text-[#1f75cb] font-medium hover:underline"
                                            >
                                                {labels.verified || "Vérifier"} <ExternalLink className="w-3 h-3" />
                                            </a>
                                        )}
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}
